<template>
  <div class="container py-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Logo</h2>
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
                Bereiche
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-detail', params: { id: $route.params.id } }" class="text-decoration-none">
                {{ selectedTeam?.name }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-settings', params: { id: $route.params.id } }" class="text-decoration-none">
                Bereich-Einstellungen
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Logo</li>
          </ol>
        </nav>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="goBack">
          <font-awesome-icon icon="arrow-left" /> Zurück
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Content -->
    <div v-else-if="selectedTeam">
      <div class="row">
        <div class="col-md-8">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <font-awesome-icon icon="image" class="me-2" />
                Logo für Quittung
              </h5>
            </div>
            <div class="card-body">
                <div class="mb-3">
                  <label for="receiptFooterImage" class="form-label fw-semibold">
                    Logo auswählen
                    <small class="text-muted">(optional)</small>
                  </label>
                  <input 
                    type="file" 
                    class="form-control" 
                    id="receiptFooterImage"
                    accept="image/png, image/jpeg"
                    @change="handleImageUpload"
                  >
                  <div class="form-text">
                    Wähle ein Logo aus, das auf der Quittung angezeigt wird. Das Bild wird auf 200x200px zugeschnitten. Empfohlen: PNG oder JPG, max. 2MB.
                  </div>
                </div>

                <!-- Vue Advanced Cropper -->
                <div v-if="showCropper" class="mb-3">
                  <label class="form-label">Logo zuschneiden (200x200 Pixel):</label>
                  <div class="cropper-wrapper">
                    <Cropper
                      ref="cropperRef"
                      :src="imagePreviewUrl"
                      :stencil-props="stencilProps"
                      :aspect-ratio="1"
                      :min-width="100"
                      :min-height="100"
                      class="cropper"
                      @change="onCropperChange"
                    />
                  </div>
                  <div class="d-flex justify-content-between mt-3">
                    <button type="button" class="btn btn-outline-secondary" @click="cancelCrop">
                      <font-awesome-icon icon="times" class="me-1" />
                      Abbrechen
                    </button>
                    <button type="button" class="btn btn-primary" @click="applyCrop">
                      <font-awesome-icon icon="crop" class="me-1" />
                      Auswahl hochladen
                    </button>
                  </div>
                </div>

                <div v-if="currentFooterImage" class="mb-3">
                  <label class="form-label">Aktuelles Logo:</label>
                  <div class="text-center">
                    <div class="logo-preview-container">
                      <img 
                        :src="currentFooterImage" 
                        alt="Aktuelles Logo" 
                        class="logo-preview"
                        @error="handleLogoError"
                      >
                    </div>
                  </div>
                  <div class="d-flex justify-content-center mt-3">
                    <button type="button" class="btn btn-outline-danger" @click="removeFooterImage">
                      <font-awesome-icon icon="trash" class="me-1" />
                      Logo entfernen
                    </button>
                  </div>
                </div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <font-awesome-icon icon="info-circle" class="me-2" />
                Hilfe
              </h6>
            </div>
            <div class="card-body">
              <p class="small text-muted">
                Hier kannst du ein Logo für deine Quittungen hochladen.
              </p>
              <ul class="small text-muted">
                <li>Das Logo wird auf der Quittung angezeigt</li>
                <li>Empfohlene Formate: PNG, JPG</li>
                <li>Das Bild wird automatisch auf 200x200px (quadratisch) zugeschnitten</li>
                <li>Du kannst den Bildausschnitt vor dem Upload anpassen</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team not found -->
    <div v-else class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Team nicht gefunden.
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { showSuccess, showError } from '@/services/notification'
import { teamService } from '@/api/teams'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()

const { selectedTeam, loading, error } = storeToRefs(teamsStore)

const receiptFooterImage = ref(null)
const currentFooterImage = ref('')
const savingLogo = ref(false)
const showCropper = ref(false)
const imagePreviewUrl = ref('')
const cropperRef = ref(null)
const originalFile = ref(null)
const cropperResult = ref(null)


const stencilProps = {
  aspectRatio: 1,
  minAspectRatio: 1,
  maxAspectRatio: 1
}


watch(() => selectedTeam.value, async (newTeam) => {
  if (newTeam) {

    if (newTeam.receiptFooterImage) {
      try {
        const logoBlob = await teamService.getTeamLogo(newTeam.id)
        currentFooterImage.value = URL.createObjectURL(logoBlob)
      } catch (error) {
        console.warn('Logo konnte nicht geladen werden:', error)
        currentFooterImage.value = ''
      }
    } else {
      currentFooterImage.value = ''
    }
  }
}, { immediate: true })

const goBack = () => {
  router.push({ name: 'teams-settings', params: { id: route.params.id } })
}

const handleImageUpload = (event) => {
  const file = event.target.files[0]
  if (file) {
    originalFile.value = file
    const reader = new FileReader()
    reader.onload = (e) => {
      imagePreviewUrl.value = e.target.result
      showCropper.value = true
    }
    reader.readAsDataURL(file)
  }
}

const handleLogoError = (event) => {

  event.target.style.display = 'none'
  showError('Logo konnte nicht geladen werden')
}

const onCropperChange = ({ coordinates, canvas }) => {
  cropperResult.value = { coordinates, canvas }
}

const applyCrop = async () => {
  if (!cropperResult.value?.canvas) {
    showError('Kein Bildausschnitt verfügbar')
    return
  }
  
  
  const scaledCanvas = document.createElement('canvas')
  const ctx = scaledCanvas.getContext('2d')
  scaledCanvas.width = 200
  scaledCanvas.height = 200
  
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(cropperResult.value.canvas, 0, 0, 200, 200)
  
  scaledCanvas.toBlob(async (blob) => {
    if (blob) {
      
      const croppedFile = new File([blob], originalFile.value.name, {
        type: originalFile.value.type,
        lastModified: Date.now()
      })
      
      receiptFooterImage.value = croppedFile
      showCropper.value = false
      cropperResult.value = null
      
      showSuccess('Bild erfolgreich zugeschnitten!')
      
      
      await saveLogo()
    }
  }, originalFile.value.type, 0.9)
}

const cancelCrop = () => {
  showCropper.value = false
  receiptFooterImage.value = null
  cropperResult.value = null
  
  
  const fileInput = document.getElementById('receiptFooterImage')
  if (fileInput) {
    fileInput.value = ''
  }
}

const removeFooterImage = async () => {
  if (!selectedTeam.value) return

  try {
    await teamService.removeTeamLogo(selectedTeam.value.id)
    

    await teamsStore.loadTeamById(selectedTeam.value.id)
    
    showSuccess('Logo erfolgreich entfernt!')
  } catch (error) {
    console.error('Fehler beim Entfernen des Logos:', error)
    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Entfernen des Logos. Bitte versuch es erneut.'
    showError(errorMessage)
  }
}

const saveLogo = async () => {
  if (!selectedTeam.value || !receiptFooterImage.value) return

  savingLogo.value = true
  try {
    await teamService.uploadTeamLogo(selectedTeam.value.id, receiptFooterImage.value)
    
    await teamsStore.loadTeamById(selectedTeam.value.id)
    
    showSuccess('Logo erfolgreich hochgeladen!')
    receiptFooterImage.value = null
  } catch (error) {
    console.error('Fehler beim Hochladen des Logos:', error)
    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Hochladen des Logos. Bitte versuch es erneut.'
    showError(errorMessage)
  } finally {
    savingLogo.value = false
  }
}

onMounted(async () => {

  if (!selectedTeam.value || selectedTeam.value.id !== route.params.id) {
    await teamsStore.loadTeamById(route.params.id)
  }
})


onUnmounted(() => {
  if (currentFooterImage.value && currentFooterImage.value.startsWith('blob:')) {
    URL.revokeObjectURL(currentFooterImage.value)
  }
  
  if (imagePreviewUrl.value && imagePreviewUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePreviewUrl.value)
  }
})
</script>

<style scoped>
.logo-preview {
  max-width: 200px;
  max-height: 200px;
  border: 2px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.5rem;
}

.logo-preview-container {
  display: inline-block;
  background: #f8f9fa;
  border-radius: 0.375rem;
  padding: 1rem;
}

.cropper-wrapper {
  height: 400px;
  border: 2px solid #dee2e6;
  border-radius: 0.375rem;
  overflow: hidden;
}

.cropper {
  height: 100%;
  background: #f8f9fa;
}


</style>
